import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import BtArrowDown from '../../assets/bt_arrow_down.svg';
import BtArrowUp from '../../assets/bt_arrow_up.svg';
import CI from '../../assets/ci.png';
import CI2 from '../../assets/ci2.png';
import menu from '../../assets/menu.svg';
import menuWhite from '../../assets/menu_white.svg';
import theme from '../../common/color/theme';
import { HorizontalLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { TitleNormalBold } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import i18n from '../../common/i18n/i18n';
import { MULTIPLE_LANGUAGE, SHOW_DEV_MENU } from '../../config';
import '../../css/headermenu.css';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useDeviceType } from '../../hooks/useDeviceType';
import { dialogStore } from '../../store/DialogStore';
import menuStore from '../../store/MenuStore';
import { fetchAndUpdateTranslations } from './FetchAndUpdateTranslations';

const KOREAN_ONLY_STARTS_WITH = ['/promotion', '/customer'];
const KOREAN_ONLY_EXACT = ['/recruit', '/recruit/benefits', '/recruit/bbpeople'];

const isKoreanOnlyPage = (pathname: string) => {
  // ① 특정 prefix로 시작하는 경우
  const isStartsWithMatch = KOREAN_ONLY_STARTS_WITH.some((prefix) =>
    pathname.startsWith(prefix)
  );

  // ② 완전히 일치하는 경우
  const isExactMatch = KOREAN_ONLY_EXACT.includes(pathname);

  return isStartsWithMatch || isExactMatch;
};

const HeaderMenuMobile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop, isPad } = useDeviceType();
  const [saveOpen, setSaveOpen] = useState(false);
  const { horizontal: padding } = useResponsivePadding();
  const { trackButtonClick } = useAnalytics();
  const isEng = ['en', 'en-mobile'].includes(i18n.language);
  const isChina = ['zh', 'zh-mobile'].includes(i18n.language);

  const currentLangName = useMemo(() => {
    switch (i18n.language) {
      case 'ko':
        return 'KOR';
      case 'ko-mobile':
        return 'KOR';
      case 'en':
        return 'ENG';
      case 'en-mobile':
        return 'ENG';
      case 'zh':
        return 'CHN';
      case 'zh-mobile':
        return 'CHN';
      default:
        return 'KOR';
    }
  }, [i18n.language]);
  const currentLang = i18n.language;

  let menuName = '';
  let sectionName = '';
  if (location.pathname === '/') {
    menuName = '회사소개';
    sectionName = 'section1';
  } else if (location.pathname.startsWith('/introduce')) {
    menuName = '회사소개';
    sectionName = 'section1';
  } else if (location.pathname.startsWith('/business')) {
    menuName = '사업영역';
    sectionName = 'section2';
  } else if (location.pathname.startsWith('/promotion')) {
    menuName = '홍보채널';
    sectionName = 'section3';
  } else if (location.pathname.startsWith('/customer')) {
    menuName = '고객지원';
    sectionName = 'section4';
  } else if (location.pathname.startsWith('/recruit')) {
    menuName = '채용';
    sectionName = 'section5';
  }

  const [activeMenu, setActiveMenu] = useState<string[]>(menuStore.mobileActiveOpenMenu);
  const [activeSection, setActiveSection] = useState<string>(sectionName);
  const [isOpen, setIsOpen] = useState(false);
  const [originalSection, setOriginalSection] = useState<string>(sectionName);
  const [scrollY, setScrollY] = useState(-1);

  const headerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [isScrollDisabled, setIsScrollDisabled] = useState(false);

  useEffect(() => {
    menuStore.setOpen(false);
  }, []);

  const getMenuName = () => {
    switch (location.pathname) {
      case '/introduce/vision':
        return t('경영비전');
      case '/introduce/ceo':
        return t('CEO 메시지');
      case '/introduce/history':
        return t('회사연혁');
      case '/introduce/qualification':
        return t('특허 및 인증');
      case '/introduce/organization':
        return t('조직도');
      case '/introduce/ci':
        return t('CI');
      case '/introduce/location':
        return t('찾아오시는 길');
      case '/business':
        return t('반도체');
      case '/business/battery':
        return t('이차전지');
      case '/business/datacenter':
        return t('데이터센터');
      case '/business/dryroom':
        return t('클린룸/드라이룸');
      case '/business/teflon':
        return t('테프론코팅');
      case '/business/cryogenic':
        return t('극저온배관');
      case '/business/ac':
        return t('자동 제관사업');
      case '/business/smartfab':
        return t('Smart Fab 설계');
      case '/promotion':
        return t('미디어센터');
      case '/promotion/news':
        return t('사내소식');
      case '/customer':
        return t('공지사항');
      case '/customer/inquiry':
        return t('문의하기');
      case '/customer/report':
        return t('BB열린소리');
      case '/recruit':
        return t('채용공고');
      case '/recruit/people':
        return t('인재상');
      case '/recruit/benefits':
        return t('복지제도');
      case '/recruit/bbpeople':
        return t('직무소개');
      default:
        return ''; // 혹은 '메뉴 없음'
    }
  };

  useEffect(() => {
    if (isScrollDisabled) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto'; // 컴포넌트 unmount 시 복구
    };
  }, [isScrollDisabled]);

  const isiOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) &&
    /Apple Computer/.test(navigator.vendor) &&
    !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
  const isIOS_Safari = isiOS && isSafari;

  if (isIOS_Safari) {
    useEffect(() => {
      if (isOpen) {
        const scrollY = window.scrollY || window.pageYOffset;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      } else {
        if (scrollY >= 0) {
          const scrollYFromTop = document.body.style.top;
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.left = '';
          document.body.style.right = '';
          document.body.style.width = '';

          const scrollToY = -parseInt(scrollYFromTop || '0');

          // requestAnimationFrame을 써서 렌더 후 바로 스크롤 복원
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollToY);
          });
        }
      }
    }, [isOpen, scrollY]);

  }

  const getMainSectionId = (href: string) => {
    return href.replace('#', '').split('-')[0]; // 항상 section1 형태만 추출
  };

  const toggleMenu = (menuName: string) => {
    setActiveMenu((prev) =>
      prev.includes(menuName)
        ? prev.filter((m) => m !== menuName) // 이미 열려있으면 닫기
        : [...prev, menuName]                // 아니면 추가
    );
    menuStore.setMobileActiveOpenMenu(activeMenu);
  };

  const handleMenuClick = (menu: string, href: string) => {
    var isMenuChanged = menuStore.currentSection !== href;
    toggleMenu(menu);
    setActiveSection(href);

    if (!isMenuChanged) {
      setIsOpen(false);
      menuStore.setOpen(false);
      menuStore.hide();
      window.location.reload();
      return;
    }
    menuStore.setCurrentSection(href);

    menuStore.setBackgroundColor('transparent');
    if (href === '#section1') {
      menuStore.setDarkMode(false);
    } else {
      menuStore.setDarkMode(true);
    }

    if (href === '#section1') {
      navigate('/');
    } else if (href === '#section1-1') {
      navigate('/introduce/vision');
    } else if (href === '#section1-2') {
      navigate('/introduce/ceo');
    } else if (href === '#section1-3') {
      navigate('/introduce/history');
    } else if (href === '#section1-4') {
      navigate('/introduce/qualification');
    } else if (href === '#section1-5') {
      navigate('/introduce/organization');
    } else if (href === '#section1-6') {
      navigate('/introduce/ci');
    } else if (href === '#section1-7') {
      navigate('/introduce/location');
    } else if (href === '#section2') {
      navigate('/business');
    } else if (href === '#section2-1') {
      navigate('/business');
    } else if (href === '#section2-2') {
      navigate('/business/battery');
    } else if (href === '#section2-3') {
      navigate('/business/datacenter');
    } else if (href === '#section2-4') {
      navigate('/business/dryroom');
    } else if (href === '#section2-5') {
      navigate('/business/teflon');
    } else if (href === '#section2-6') {
      navigate('/business/cryogenic');
    } else if (href === '#section2-7') {
      navigate('/business/ac');
    } else if (href === '#section2-8') {
      navigate('/business/smartfab');
    } else if (href === '#section3') {
      navigate('/promotion');
    } else if (href === '#section3-1') {
      navigate('/promotion');
    } else if (href === '#section3-2') {
      navigate('/promotion/news');
    } else if (href === '#section4') {
      navigate('/customer/inquiry');
    } else if (href === '#section4-1') {
      navigate('/customer/inquiry');
    } else if (href === '#section4-2') {
      navigate('/customer/report');
    } else if (href === '#section5') {
      if (isEng || isChina) {
        navigate('/recruit/people');
      } else {
        navigate('/recruit');
      }
    } else if (href === '#section5-1') {
      if (isEng || isChina) {
        navigate('/recruit/people');
      } else {
        navigate('/recruit');
      }
    } else if (href === '#section5-2') {
      navigate('/recruit/people');
    } else if (href === '#section5-3') {
      navigate('/recruit/benefits');
    } else if (href === '#section5-4') {
      navigate('/recruit/bbpeople');
    }
  };

  const getMargin = (ko: number, en: number, ja: number, zh: number): number => {
    switch (currentLang) {
      case 'en':
        return en;
      case 'ja':
        return ja;
      case 'zh':
        return zh;
      case 'ko':
      default:
        return ko;
    }
  };

  const menuData = [
    {
      name: t('회사소개'),
      gaName: '회사소개',
      href: '#section1',
      marginRight: getMargin(isDesktop ? 52 : 52, 8, 12, 70),
      subs: [
        t('경영비전'),
        t('CEO 메시지'),
        t('회사연혁'),
        t('특허 및 인증'),
        t('조직도'),
        t('CI'),
        t('찾아오시는 길'),
      ],
      gaNames: [
        '경영비전',
        'CEO인사말',
        '회사연혁',
        '특허및인증',
        '조직도',
        'CI',
        '찾아오시는길',
      ]
    },
    {
      name: t('사업영역'),
      gaName: '사업영역',
      href: '#section2',
      marginRight: getMargin(isDesktop ? 28 : 30, 58, 8, 16),
      subs: [
        t('반도체'),
        t('이차전지'),
        t('데이터센터'),
        t('클린룸/드라이룸'),
        t('테프론코팅'),
        t('극저온배관'),
      ],
      gaNames: [
        '반도체',
        '이차전지',
        '데이터센터',
        '클린룸드라이룸',
        '테프론코팅',
        '극저온배관',
      ]
    },
    ...((!isEng && !isChina)
      ? [{
        name: t('홍보채널'),
        gaName: '홍보채널',
        href: '#section3',
        marginRight: getMargin(isDesktop ? 77 : 76, 148, 110, 106),
        subs: [
          t('미디어센터'),
          t('사내소식'),
        ],
        gaNames: [
          '미디어센터',
          '사내소식',
        ]
      }]
      : []
    ),
    ...((!isEng && !isChina)
      ? [{
        name: t('고객지원'),
        gaName: '고객지원',
        href: '#section4',
        marginRight: getMargin(isDesktop ? 97 : 100, 153, 160, 93),
        subs: [
          t('문의하기'),
          t('BB열린소리'),
        ],
        gaNames: [
          '문의하기',
          'BB열린소리',
        ]
      }]
      : []
    ),
    {
      name: t('채용'),
      gaName: '채용',
      href: '#section5',
      marginRight: getMargin(isDesktop ? 94 : 98, 37, 56, 98),
      subs: (isEng || isChina)
        ? [t('인재상')]
        : [
          t('채용공고'),
          t('인재상'),
          t('복지제도'),
          t('직무소개'),
        ],
      gaNames: (isEng || isChina)
        ? ['인재상']
        : [
          '채용공고',
          '인재상',
          '복지제도',
          '직무소개',
        ]
    },
    ...(MULTIPLE_LANGUAGE
      ? [{
        name: currentLangName,
        gaName: currentLangName,
        href: '#section6',
        marginRight: getMargin(isDesktop ? 85 : 114, 85, 85, 85),
        subs: [
          { label: t('ENG'), lang: 'en' },
          { label: t('CHN'), lang: 'zh' },
          { label: t('KOR'), lang: 'ko' },
          ...(SHOW_DEV_MENU ? [{ label: t('UPDATE'), lang: 'dev' }, { label: t('CLEAR'), lang: 'clear' }] : []),
        ],
      }]
      : []
    ),
  ];


  const logo = menuStore.isDarkMode ? CI2 : CI;
  return (
    <>
      <header
        ref={headerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '69px',
          backgroundColor: isOpen ? 'white' : 'transparent',
          color: menuStore.isDarkMode ? 'white' : 'black',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
          zIndex: 1000,
          overflow: 'hidden',
          borderBottom: isOpen
            ? '1px solid #F0F1F2'
            : '1px solid #ffffff88',
        }}
      >
        <img src={logo}
          style={{ marginTop: '36px', cursor: 'pointer', width: '120px', height: '16px', marginLeft: padding }}
          onClick={() => {
            menuStore.setCurrentSection('#section1');
            navigate('/');
          }}
          data-analytics-name="메인_로고"
        />

        <div onClick={() => {
          if (isOpen) {
            setIsOpen(false);
            menuStore.setOpen(false);
            menuStore.setBackgroundColor('transparent');
            menuStore.setDarkMode(saveOpen);
            // if (location.pathname === '/' || location.pathname === '/terms' || location.pathname === '/terms/policy') {
            //   menuStore.setDarkMode(false);
            // } else {
            //   menuStore.setDarkMode(true);
            // }
            setIsScrollDisabled(false);
          } else {
            setIsOpen(true);
            menuStore.setOpen(true);
            setSaveOpen(menuStore.isDarkMode);
            setScrollY(window.scrollY);

            menuStore.setBackgroundColor('white');
            menuStore.setDarkMode(false);
            setIsScrollDisabled(true);
          }

        }}>
          <img src={menuStore.isDarkMode ? menuWhite : menu}
            style={{ marginRight: padding, marginTop: '38px', cursor: 'pointer' }}
          />
        </div>


      </header>

      {/* ✅ 메뉴 영역 */}
      {isOpen && (
        <div
          ref={menuRef}
          className={`menu-content ${isOpen ? 'menu-content-open' : ''}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: 0,
            width: '100%',
            minHeight: 'calc(100dvh - 90px)',
            backgroundColor: menuStore.backgroundColor,
            paddingTop: '90px',
            paddingBottom: isDesktop ? '4rem' : '2rem',
            zIndex: 999,
            overflowY: 'auto',
            boxShadow: 'none',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          {menuData.map((menu, idx) => (
            <div key={menu.name} style={{ paddingLeft: '0px' }}>
              <BTBox height={2} />
              <VerticalLayout style={{ width: '100%' }} onClick={() => {
                // Analytics 이벤트를 먼저 전송
                trackButtonClick(`메뉴_${menu.gaName || ''}`, `main-메뉴_${menu.gaName || ''}`, {
                  button_text: menu.name,
                  button_location: 'div',
                  button_type: 'click',
                  page_name_korean: '메인'
                });
                // 그 다음 메뉴 토글
                toggleMenu(menu.name);
              }}>
                <HorizontalLayout style={{ justifyContent: 'space-between', paddingLeft: padding + 5, paddingRight: padding + 5, alignItems: 'center' }}>
                  <TitleNormalBold style={{ flex: 1, fontSize: '18px', height: '50px', lineHeight: '50px', fontFamily: isChina ? theme.typography.fontFamily : undefined }}>{menu.name}</TitleNormalBold>
                  {activeMenu.includes(menu.name) ? <img src={BtArrowUp} style={{ width: '30px', height: '30px' }} /> : <img src={BtArrowDown} style={{ width: '30px', height: '30px' }} />}
                </HorizontalLayout>
              </VerticalLayout>

              {activeMenu.includes(menu.name) && (
                <div
                  style={{
                    marginTop: '16px',
                    paddingRight: '20px',
                    fontSize: '16px',
                    color: '#444',
                    textAlign: 'left',
                    fontFamily: isChina ? theme.typography.fontFamily : undefined,
                  }}
                >
                  {menu.subs.map((sub, index) => {
                    const subHref = `${menu.href}-${index + 1}`;

                    if (typeof sub === 'object' && 'lang' in sub) {
                      return (
                        <div key={index} style={{ marginBottom: '16px', paddingLeft: padding + 5 }}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              // Analytics 이벤트를 먼저 전송
                              trackButtonClick(`메뉴_${sub.label}`, `main-메뉴_${sub.label}`, {
                                button_text: sub.label,
                                button_location: 'a',
                                button_type: 'click',
                                page_name_korean: '메인'
                              });
                              const targetLang = sub.lang;
                              const isTargetKorean = targetLang === 'ko' || targetLang === 'ko-mobile';
                              const isCurrentKorean = ['ko', 'ko-mobile'].includes(i18n.language);

                              if (!isTargetKorean && isCurrentKorean && isKoreanOnlyPage(location.pathname)) {
                                let title = '';
                                let message = '';
                                let confirmButtonTitle = '';
                                switch (targetLang) {
                                  case 'en':
                                  case 'en-mobile':
                                    title = 'Notice';
                                    message = 'This page is only available in Korean.';
                                    confirmButtonTitle = 'OK';
                                    break;
                                  case 'zh':
                                  case 'zh-mobile':
                                    title = '提示';
                                    message = '此页面仅提供韩语版本。';
                                    confirmButtonTitle = '确认';
                                    break;
                                  default:
                                    title = '알림';
                                    message = '해당 페이지는 한국어에서만 제공됩니다.';
                                    confirmButtonTitle = '확인';
                                    break;
                                }

                                dialogStore.showDialog({
                                  title: title,
                                  message,
                                  confirmButtonTitle: confirmButtonTitle,
                                  isCaution: false,
                                  onClose: () => {
                                    i18n.changeLanguage(targetLang);
                                    navigate('/');
                                  },
                                  onConfirm: () => {
                                    i18n.changeLanguage(targetLang);
                                    navigate('/');
                                  }
                                });

                                return;

                                // const confirmed = window.confirm(
                                //   '현재 페이지는 한국어에서만 제공됩니다.\n홈으로 이동한 후 선택하신 언어로 변경하시겠습니까?',
                                // );
                                // if (!confirmed) {
                                //   return;
                                // }
                                // navigate('/');
                              }

                              // 그 다음 언어 변경 처리
                              if (SHOW_DEV_MENU) {
                                if (targetLang === 'dev') {
                                  fetchAndUpdateTranslations();
                                } else if (targetLang === 'clear') {
                                  localStorage.removeItem("translations");
                                  window.location.reload();
                                } else {
                                  // 언어 변경
                                  i18n.changeLanguage(targetLang);
                                  window.location.reload();
                                }
                              } else {
                                i18n.changeLanguage(targetLang);
                              }
                            }}
                            style={{ color: '#444', fontFamily: isChina ? theme.typography.fontFamily : undefined }}
                            data-analytics-name={`메뉴_${sub.label}`}
                          >
                            {sub.label}
                          </a>
                        </div>
                      );
                    }

                    return (
                      <div key={index} style={{ marginBottom: '16px', paddingLeft: padding + 5 }}>
                        <a
                          href={subHref}
                          onClick={(e) => {
                            e.preventDefault();
                            // Analytics 이벤트를 먼저 전송
                            trackButtonClick(`메뉴_${menu.gaNames?.[index] || ''}`, `main-메뉴_${menu.gaNames?.[index] || ''}`, {
                              button_text: sub,
                              button_location: 'a',
                              button_type: 'click',
                              page_name_korean: '메인'
                            });
                            // 그 다음 메뉴 처리
                            handleMenuClick(menu.name, subHref);
                            setIsOpen(false);
                            menuStore.setOpen(false);
                          }}
                          style={{ color: sub === getMenuName() ? theme.colors.BIBI : '#444', fontWeight: sub === getMenuName() ? 700 : 400, fontFamily: isChina ? theme.typography.fontFamily : undefined }}
                          data-analytics-name={`메뉴_${menu.gaNames?.[index] || ''}`}
                        >
                          {sub}
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
              <BTBox height={8} />
              <VerticalLayout style={{ paddingLeft: '20px', paddingRight: '20px' }}>
                <div style={{ width: '100%', height: '1px', backgroundColor: 'black', opacity: 0.2 }} />
              </VerticalLayout>
            </div>
          ))}
        </div>
      )
      }
    </>
  );
};

export default observer(HeaderMenuMobile);
